import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrgKey } from '@reflex/ui/client/context';
import { githubApi } from './api.js';
import { isNewerPullRequestDetails } from './pull-request-order.js';
import type { PullRequestDetails, PullRequestRef } from './useGitHubRepos.js';

// Re-exported so host surfaces (e.g. `AgentAssociationChips`) can pull the ref
// shape from the same entry point as the provider that consumes it.
export type { PullRequestRef } from './useGitHubRepos.js';

/**
 * Context offered by {@link PullRequestBatchProvider} so the eager PR chips on a
 * focused agent surface resolve their live state through a single batched
 * `pulls/batch` request instead of N individual `pulls/:number` fetches — the
 * Sentry `AGENT-FLOW-WEB-4F` / `4G` N+1.
 *
 * A chip's `usePullRequestDetails` reads this context when present and its PR is
 * in scope, and reads its `data` / loading state from the batch instead of its
 * own query (which is held off for the in-flight batch — see the gate in
 * `useGitHubRepos.ts`). Merged/closed chips (which only fetch on hover) and
 * list-row strips (which verify lazily) are out of scope and fall back to the
 * existing single-PR path unchanged.
 */
export interface PullRequestBatchContextValue {
  /** True while the batch is the single source of this PR's details and the
   * chip's own single-PR query should stay disabled so the batch is the only
   * fetch. Flips false once the batch settles AND it returned data for this PR
   * (a settled-but-missing PR un-gates so the chip falls back to a single-PR
   * fetch). Out-of-scope PRs are never tracking. */
  isTracking(pr: PullRequestRef): boolean;
  /** True while the batch for this PR is still in flight — surfaced as the
   * chip's loading state so it keeps its greyed "verifying" look through the
   * batch window. */
  isFetching(pr: PullRequestRef): boolean;
  /** The newer of the batch-resolved details for this PR and any live
   * `pr_updated` push that arrived after the batch resolved. `undefined`
   * before the batch settles or for a PR the batch didn't return (404). */
  detailsFor(pr: PullRequestRef): PullRequestDetails | undefined;
  /** Report a live `pr_updated` push so the overlay refreshes the chip without
   * a refetch — beating the (possibly pre-push) batch result, unless the push
   * is older than what the chip already holds. */
  reportUpdate(pr: PullRequestRef, details: PullRequestDetails): void;
}

export const PullRequestBatchContext = createContext<PullRequestBatchContextValue | null>(null);

/** Stable, case-insensitive key for a PR ref (owner/repo are case-insensitive on
 * GitHub and the stored PR URL may carry non-canonical casing). */
function prKey(pr: PullRequestRef): string {
  return `${pr.owner.toLowerCase()}/${pr.repo.toLowerCase()}/${pr.number}`;
}

/**
 * Inner scope, keyed by {@link PullRequestBatchProvider} so a change to the
 * open-PR set remounts it — resetting the live-update `overlay` state cleanly
 * without a reset effect (no `setState`-in-effect cascade). Holds the batch
 * `useQuery`, the overlay, and the context value.
 */
function BatchScope({
  prs,
  scopeKey,
  children,
}: {
  prs: PullRequestRef[];
  scopeKey: string;
  children: ReactNode;
}) {
  const orgKey = useOrgKey();
  // The in-scope refs (lower-cased owner/repo) for `isTracking` membership.
  // Derived from the same `scopeKey` the parent keyed on, so it is stable
  // across the parent's re-renders (only recomputed when the scope changes).
  const scope = useMemo(() => new Set(scopeKey ? scopeKey.split(',') : []), [scopeKey]);

  const query = useQuery<Map<string, PullRequestDetails>>({
    queryKey: ['github-pr-details-batch', orgKey, ...scopeKey.split(',')],
    queryFn: async () => {
      // Group by owner/repo so a cross-repo strip issues one batch per repo.
      // Most focused agent pages are single-repo (the N+1 Sentry pattern is
      // `/pulls/*` under one repo), so the common case is a single batch call.
      const byRepo = new Map<string, { owner: string; repo: string; numbers: number[] }>();
      for (const p of prs) {
        const k = `${p.owner.toLowerCase()}/${p.repo.toLowerCase()}`;
        const entry = byRepo.get(k) ?? { owner: p.owner, repo: p.repo, numbers: [] };
        entry.numbers.push(p.number);
        byRepo.set(k, entry);
      }
      const out = new Map<string, PullRequestDetails>();
      await Promise.all(
        [...byRepo.values()].map(async ({ owner, repo, numbers }) => {
          const unique = Array.from(new Set(numbers));
          const { pulls } = await githubApi.resolvePullRequestsBatch(owner, repo, unique);
          for (const pr of pulls) {
            out.set(`${owner.toLowerCase()}/${repo.toLowerCase()}/${pr.number}`, {
              title: pr.title,
              state: pr.state,
              merged: pr.merged,
              draft: pr.draft,
              headBranch: pr.headBranch,
              baseBranch: pr.baseBranch,
              updatedAt: pr.updatedAt,
            });
          }
        }),
      );
      return out;
    },
    enabled: prs.length > 0,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    // A failed batch un-gates every chip (it falls back to a single-PR fetch),
    // so don't keep retrying — the per-PR path is the graceful degradation.
    retry: false,
  });

  // Live `pr_updated` overlay, kept separate from the batch result so a push
  // that arrives after the batch resolves beats its (possibly pre-push) value.
  // Reset on a scope change is handled by the remount — no reset effect needed.
  const [overlay, setOverlay] = useState<Map<string, PullRequestDetails>>(new Map());

  const settled = !query.isPending && !query.isFetching;
  const batchData = query.data;

  const value = useMemo<PullRequestBatchContextValue>(
    () => ({
      isTracking: (pr) => {
        const k = prKey(pr);
        if (!scope.has(k)) return false;
        // While the batch is in flight, hold every in-scope chip on the batch.
        // Once settled, keep holding only the PRs the batch returned — a
        // settled-but-missing PR un-gates so the chip falls back to its own
        // single-PR fetch (and the live webhook receiver keeps it honest).
        if (!settled) return true;
        return !!batchData?.has(k);
      },
      isFetching: (pr) => scope.has(prKey(pr)) && !settled,
      detailsFor: (pr) => {
        const pushed = overlay.get(prKey(pr));
        const fetched = batchData?.get(prKey(pr));
        // A refetched batch can be newer than an earlier push.
        if (!pushed || !fetched) return pushed ?? fetched;
        return isNewerPullRequestDetails(fetched, pushed) ? fetched : pushed;
      },
      reportUpdate: (pr, details) => {
        const k = prKey(pr);
        setOverlay((prev) => {
          // A push older than what the chip already shows (webhooks arrive
          // out of order) leaves it alone.
          if (!isNewerPullRequestDetails(details, prev.get(k) ?? batchData?.get(k))) return prev;
          const next = new Map(prev);
          next.set(k, details);
          return next;
        });
      },
    }),
    [scope, settled, batchData, overlay],
  );

  return (
    <PullRequestBatchContext.Provider value={value}>{children}</PullRequestBatchContext.Provider>
  );
}

/**
 * Resolve the live state of every `open` PR ref in `prs` through a single
 * `POST /github/repos/:owner/:repo/pulls/batch` round trip per repo (cross-repo
 * strips fan out, but a strip is overwhelmingly single-repo), and expose the
 * result — overlaid with live `pr_updated` pushes — to the PR chips inside via
 * {@link PullRequestBatchContext}.
 *
 * Render this only on the eager path (the focused stream header / detail
 * identity); list-row strips pass `eagerVerifyOpenPrs={false}` and must NOT
 * render the provider so their chips fall back to the existing per-PR path.
 * With no open PRs there is nothing to batch — the parent renders the children
 * bare instead of mounting this provider.
 */
export function PullRequestBatchProvider({
  prs,
  children,
}: {
  prs: PullRequestRef[];
  children: ReactNode;
}) {
  // A stable, deduped, sorted key string so a re-render of the same open-PR
  // strip produces the same `BatchScope` `key` (the parent may hand a fresh
  // array each render — the value-equal string keeps the same scope mounted,
  // and a scope change remounts it cleanly, resetting the live-overlay state).
  const scopeKey = useMemo(
    () =>
      Array.from(new Set(prs.map(prKey)))
        .sort()
        .join(','),
    [prs],
  );
  return (
    <BatchScope key={scopeKey} prs={prs} scopeKey={scopeKey}>
      {children}
    </BatchScope>
  );
}

/**
 * Read the active batch context, if any. `null` outside a provider — the hook
 * falls back to the existing single-PR path.
 */
export function usePullRequestBatch(): PullRequestBatchContextValue | null {
  return useContext(PullRequestBatchContext);
}
